/**
 * Influencer Landing Page
 * 
 * A branded landing page for influencers to share with their followers.
 * Features dynamic theming based on influencer branding settings.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInfluencer, InfluencerData } from '../contexts/InfluencerContext';
import { useSignInModalContext } from '../contexts/SignInModalContext';
import {
  Target,
  ArrowRight,
  Sparkles,
  ChevronDown,
  BarChart3,
  PlayCircle,
  TrendingUp,
  Zap,
  Users,
  ShieldCheck,
  Twitter,
  Youtube,
  Instagram,
  Globe,
  Award,
} from 'lucide-react';

const InfluencerLandingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { influencer, isLoading, error, loadInfluencerBySlug } = useInfluencer();
  const { openSignInModal, isSignedIn } = useSignInModalContext();
  const [localInfluencer, setLocalInfluencer] = useState<InfluencerData | null>(null);

  useEffect(() => {
    if (slug) {
      loadInfluencerBySlug(slug).then((data) => {
        if (data) {
          setLocalInfluencer(data);
        }
      });
    }
  }, [slug, loadInfluencerBySlug]);

  const displayInfluencer = localInfluencer || influencer;

  // Note: We no longer auto-redirect signed-in users
  // The landing page should always be shown when visiting an influencer link
  // Users can click "Launch App" to go to the main app

  // Get branding colors with fallbacks
  const primaryColor = displayInfluencer?.branding?.primaryColor || '#3b82f6';
  const secondaryColor = displayInfluencer?.branding?.secondaryColor || '#8b5cf6';
  const accentColor = displayInfluencer?.branding?.accentColor || '#10b981';

  const handleGetStarted = () => {
    if (isSignedIn) {
      navigate('/');
    } else {
      openSignInModal();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state - influencer not found
  if (error || !displayInfluencer) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Page Not Found</h2>
          <p className="text-slate-400 mb-6">
            The influencer page you're looking for doesn't exist or is no longer available.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all"
          >
            Go to TradeRewind
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center px-4 py-20"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}10 50%, ${accentColor}05 100%)`,
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, ${primaryColor}20 0%, transparent 50%), 
                               radial-gradient(circle at 75% 75%, ${secondaryColor}20 0%, transparent 50%)`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          {/* Influencer Badge */}
          <div className="inline-flex items-center gap-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-full px-4 py-2 mb-8">
            {displayInfluencer.profileImage ? (
              <img 
                src={displayInfluencer.profileImage} 
                alt={displayInfluencer.name}
                className="w-10 h-10 rounded-full object-cover border-2"
                style={{ borderColor: primaryColor }}
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {displayInfluencer.name.charAt(0)}
              </div>
            )}
            <span className="text-slate-300">
              Recommended by <span className="text-white font-semibold">{displayInfluencer.name}</span>
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">Master the Markets with</span>
            <br />
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
            >
              TradeRewind
            </span>
          </h1>

          {/* Tagline */}
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto">
            {displayInfluencer.tagline || 'Advanced AI-powered trading analysis and market replay to sharpen your trading skills'}
          </p>

          {/* Testimonial */}
          {displayInfluencer.testimonial && (
            <div className="max-w-2xl mx-auto mb-10">
              <blockquote 
                className="text-lg md:text-xl italic text-slate-200 p-6 rounded-2xl border"
                style={{ 
                  backgroundColor: `${primaryColor}10`,
                  borderColor: `${primaryColor}30`,
                }}
              >
                "{displayInfluencer.testimonial}"
                <footer className="mt-4 text-sm text-slate-400 not-italic">
                  — {displayInfluencer.name}
                </footer>
              </blockquote>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={handleGetStarted}
              className="group px-8 py-4 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 flex items-center gap-2 shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                boxShadow: `0 10px 40px ${primaryColor}40`,
              }}
            >
              {isSignedIn ? 'Launch App' : 'Get Started Free'}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            {displayInfluencer.specialOffer && (
              <div 
                className="px-6 py-3 rounded-xl border text-sm"
                style={{ 
                  borderColor: accentColor,
                  color: accentColor,
                }}
              >
                <Sparkles className="w-4 h-4 inline mr-2" />
                {displayInfluencer.specialOffer.title || `${displayInfluencer.specialOffer.discountPercent}% OFF`}
                {displayInfluencer.specialOffer.discountCode && (
                  <span className="ml-2 font-mono bg-slate-800 px-2 py-1 rounded">
                    {displayInfluencer.specialOffer.discountCode}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Influencer Stats */}
          {displayInfluencer.stats && (
            <div className="flex flex-wrap justify-center gap-8 text-center">
              {displayInfluencer.stats.followers && (
                <div>
                  <div className="text-3xl font-bold" style={{ color: primaryColor }}>
                    {displayInfluencer.stats.followers}
                  </div>
                  <div className="text-slate-400 text-sm">Followers</div>
                </div>
              )}
              {displayInfluencer.stats.experience && (
                <div>
                  <div className="text-3xl font-bold" style={{ color: secondaryColor }}>
                    {displayInfluencer.stats.experience}
                  </div>
                  <div className="text-slate-400 text-sm">Trading Experience</div>
                </div>
              )}
              {displayInfluencer.stats.trades && (
                <div>
                  <div className="text-3xl font-bold" style={{ color: accentColor }}>
                    {displayInfluencer.stats.trades}
                  </div>
                  <div className="text-slate-400 text-sm">Trades Analyzed</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-slate-500" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why {displayInfluencer.name} Recommends TradeRewind
          </h2>
          <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto">
            Professional-grade tools used by serious traders to analyze markets and improve their skills
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div 
              className="p-6 rounded-2xl border transition-all hover:scale-105"
              style={{ 
                backgroundColor: `${primaryColor}05`,
                borderColor: `${primaryColor}20`,
              }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <BarChart3 className="w-6 h-6" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI Chart Analysis</h3>
              <p className="text-slate-400">
                Upload any chart and get instant AI-powered analysis with pattern recognition and trade signals.
              </p>
            </div>

            {/* Feature 2 */}
            <div 
              className="p-6 rounded-2xl border transition-all hover:scale-105"
              style={{ 
                backgroundColor: `${secondaryColor}05`,
                borderColor: `${secondaryColor}20`,
              }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${secondaryColor}20` }}
              >
                <PlayCircle className="w-6 h-6" style={{ color: secondaryColor }} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Market Replay</h3>
              <p className="text-slate-400">
                Practice trading with historical data. Replay any market session and test your strategies risk-free.
              </p>
            </div>

            {/* Feature 3 */}
            <div 
              className="p-6 rounded-2xl border transition-all hover:scale-105"
              style={{ 
                backgroundColor: `${accentColor}05`,
                borderColor: `${accentColor}20`,
              }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <TrendingUp className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Live Market Data</h3>
              <p className="text-slate-400">
                Real-time charts with professional indicators. Track stocks, futures, and crypto in one place.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 transition-all hover:scale-105">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Trading Signals</h3>
              <p className="text-slate-400">
                Get buy/sell signals based on technical analysis and AI pattern recognition.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 transition-all hover:scale-105">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Paper Trading</h3>
              <p className="text-slate-400">
                Practice with virtual money. Track your performance and improve without risking real capital.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 transition-all hover:scale-105">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Secure & Private</h3>
              <p className="text-slate-400">
                Your data is encrypted and never shared. Trade with confidence knowing your information is safe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Influencer Section */}
      {displayInfluencer.bio && (
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div 
              className="p-8 md:p-12 rounded-3xl border"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}05)`,
                borderColor: `${primaryColor}20`,
              }}
            >
              <div className="flex flex-col md:flex-row items-center gap-8">
                {displayInfluencer.profileImage ? (
                  <img 
                    src={displayInfluencer.profileImage} 
                    alt={displayInfluencer.name}
                    className="w-32 h-32 rounded-full object-cover border-4"
                    style={{ borderColor: primaryColor }}
                  />
                ) : (
                  <div 
                    className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {displayInfluencer.name.charAt(0)}
                  </div>
                )}
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold mb-2">About {displayInfluencer.name}</h3>
                  <p className="text-slate-300 mb-4">{displayInfluencer.bio}</p>
                  
                  {/* Social Links */}
                  {displayInfluencer.socialLinks && (
                    <div className="flex gap-4 justify-center md:justify-start">
                      {displayInfluencer.socialLinks.twitter && (
                        <a 
                          href={displayInfluencer.socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          <Twitter className="w-5 h-5" />
                        </a>
                      )}
                      {displayInfluencer.socialLinks.youtube && (
                        <a 
                          href={displayInfluencer.socialLinks.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          <Youtube className="w-5 h-5" />
                        </a>
                      )}
                      {displayInfluencer.socialLinks.instagram && (
                        <a 
                          href={displayInfluencer.socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {displayInfluencer.socialLinks.website && (
                        <a 
                          href={displayInfluencer.socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          <Globe className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section 
        className="py-20 px-4"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}20 0%, ${secondaryColor}15 100%)`,
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <Award className="w-16 h-16 mx-auto mb-6" style={{ color: primaryColor }} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Level Up Your Trading?
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            Join thousands of traders who use TradeRewind to analyze markets and improve their skills.
          </p>
          
          <button
            onClick={handleGetStarted}
            className="group px-10 py-5 rounded-xl font-semibold text-xl transition-all transform hover:scale-105 flex items-center gap-3 mx-auto shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              boxShadow: `0 10px 40px ${primaryColor}40`,
            }}
          >
            {isSignedIn ? 'Launch App' : 'Start Trading Smarter'}
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>

          {displayInfluencer.specialOffer?.description && (
            <p className="mt-6 text-sm" style={{ color: accentColor }}>
              {displayInfluencer.specialOffer.description}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-slate-500 text-sm">
            © {new Date().getFullYear()} TradeRewind. All rights reserved.
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span>Partner page for</span>
            <span className="text-white font-semibold">{displayInfluencer.name}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InfluencerLandingPage;
